import { alignTranscript } from '../lib/align.ts';
import type { AssessmentResult } from '../types.ts';
import { createAzureAssessor, createTokenProvider } from './azure.ts';
import type { SdkLoader, TokenProvider } from './azure.ts';
import { AssessError } from './types.ts';
import type { AssessHooks, Assessor } from './types.ts';

// --- Web Speech API (zapasowy tryb: transkrypt + dopasowanie słów) ----------------------------

interface RecAlt {
  transcript: string;
}
interface RecEvent {
  results: ArrayLike<ArrayLike<RecAlt>>;
}
interface RecErrorEvent {
  error: string;
}
interface Recognition {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onaudiostart: (() => void) | null;
  onresult: ((e: RecEvent) => void) | null;
  onerror: ((e: RecErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  abort(): void;
}
type RecognitionCtor = new () => Recognition;

export function getRecognitionCtor(scope: object = globalThis): RecognitionCtor | null {
  const s = scope as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return s.SpeechRecognition ?? s.webkitSpeechRecognition ?? null;
}

/** Wybiera z alternatyw rozpoznawania tę, która najlepiej pasuje do zdania wzorcowego. */
export function bestAlternative(reference: string, alternatives: readonly string[]): AssessmentResult {
  let best: AssessmentResult | null = null;
  for (const alt of alternatives) {
    const r = alignTranscript(reference, alt);
    if (!best || r.accuracy * r.completeness > best.accuracy * best.completeness) best = r;
  }
  return best ?? alignTranscript(reference, '');
}

/** Zamienia kod błędu Web Speech API na nasz AssessError (z kodem błędu w komunikacie — do diagnostyki). */
export function errorFromRecognition(code: string): AssessError {
  const detail = `SpeechRecognition error="${code}"`;
  switch (code) {
    case 'no-speech':
    case 'aborted':
      return new AssessError('no-speech', detail);
    case 'not-allowed':
    case 'service-not-allowed':
      return new AssessError('mic-denied', detail);
    case 'audio-capture':
      return new AssessError('no-mic', detail);
    case 'language-not-supported':
      return new AssessError('unsupported', detail);
    default:
      return new AssessError('network', detail); // "network" i nieznane błędy
  }
}

export function createBrowserAssessor(scope: object = globalThis): Assessor | null {
  const Ctor = getRecognitionCtor(scope);
  if (!Ctor) return null;
  return {
    name: 'browser',
    assess(reference, hooks) {
      return new Promise<AssessmentResult>((resolve, reject) => {
        const rec = new Ctor();
        rec.lang = 'en-US';
        rec.interimResults = false;
        rec.maxAlternatives = 3;
        rec.continuous = false;
        let settled = false;
        let audioStarted = false;
        rec.onaudiostart = () => {
          audioStarted = true;
          hooks?.onReady?.(); // przeglądarka zaczęła nagrywać — można mówić
        };
        rec.onresult = (e) => {
          settled = true;
          const first = e.results[0];
          const alts: string[] = [];
          if (first) for (let i = 0; i < first.length; i++) alts.push(first[i]?.transcript ?? '');
          resolve(bestAlternative(reference, alts));
        };
        rec.onerror = (e) => {
          settled = true;
          reject(errorFromRecognition(e.error));
        };
        rec.onend = () => {
          if (settled) return;
          settled = true;
          // koniec bez wyniku i bez błędu: jeśli nawet nie ruszyło nagrywanie, to problem z mikrofonem, nie z głosem dziecka
          reject(audioStarted ? new AssessError('no-speech', 'koniec bez wyniku') : new AssessError('no-mic', 'przeglądarka zakończyła nasłuch bez uruchomienia mikrofonu (brak zdarzenia audiostart)'));
        };
        try {
          rec.start();
        } catch (e) {
          reject(new AssessError('unsupported', e instanceof Error ? e.message : String(e)));
        }
      });
    },
  };
}

// --- samoocena (ostatnia deska ratunku, gdy nic nie działa) -----------------------------------

/** Wynik samooceny dziecka: nigdy 3 gwiazdki — bo nikt tu nie sprawdził wymowy. */
export function selfResult(ok: boolean): AssessmentResult {
  return { source: 'self', completeness: 100, accuracy: ok ? 80 : 40, words: [] };
}

// --- łańcuch metod ------------------------------------------------------------------------------

/** Dlaczego Azure jest (`ok`) albo go nie ma: serwer nie zna urządzenia (`unpaired`), dzienny limit (`limit`), brak serwera lub kluczy (`off`). */
export type AzureStatus = 'ok' | 'unpaired' | 'limit' | 'off';

export interface AssessorChain {
  assessors: Assessor[];
  /** Najlepsza dostępna metoda (do pokazania w ustawieniach). */
  primary: Assessor['name'] | 'self';
  /** Wynik sondy Azure (brak = sonda jeszcze trwa). */
  azure?: AzureStatus;
  /** Dlaczego Azure nie działa (kod HTTP i komunikat serwera) — do pokazania rodzicowi. */
  azureDetail?: string;
}

/**
 * Buduje listę metod oceny w kolejności: Azure (jeśli /api/token odpowiada) → przeglądarka → samoocena.
 * Samoocena nie jest Assessorem — UI wchodzi w nią, gdy `runAssessment` rzuci 'unavailable'.
 */
export async function createAssessorChain(opts: {
  tokens?: TokenProvider;
  sdkLoader?: SdkLoader;
  scope?: object;
  probeAzure?: boolean;
} = {}): Promise<AssessorChain> {
  const assessors: Assessor[] = [];
  const tokens = opts.tokens ?? createTokenProvider();
  let azure: AzureStatus = 'off';
  let azureDetail: string | undefined;
  if (opts.probeAzure !== false) {
    try {
      await tokens.get(); // jeśli token da się pobrać, serwer jest skonfigurowany
      assessors.push(createAzureAssessor(tokens, opts.sdkLoader));
      azure = 'ok';
    } catch (e) {
      // brak backendu / kluczy — spadamy na przeglądarkę; „unpaired” i „limit” zapamiętujemy, żeby ekran rodzica podpowiedział, co zrobić
      azure = e instanceof AssessError && (e.code === 'unpaired' || e.code === 'limit') ? e.code : 'off';
      azureDetail = e instanceof Error ? e.message : String(e);
    }
  }
  const browser = createBrowserAssessor(opts.scope);
  if (browser) assessors.push(browser);
  return { assessors, primary: assessors[0]?.name ?? 'self', azure, ...(azureDetail ? { azureDetail } : {}) };
}

/**
 * Próbuje kolejne metody. Błędy "no-speech", "mic-denied" i "no-mic" wracają do UI od razu (to nie wina metody,
 * a kolejna metoda użyłaby tego samego mikrofonu); pozostałe (sieć, SDK, token) przechodzą do następnej metody.
 * Gdy nic nie działa: AssessError('unavailable') z listą powodów wszystkich metod.
 */
export async function runAssessment(chain: AssessorChain, reference: string, hooks?: AssessHooks): Promise<AssessmentResult> {
  const failures: string[] = [];
  const causes: AssessError['code'][] = [];
  for (const a of chain.assessors) {
    try {
      const result = await a.assess(reference, hooks);
      // główna metoda zawiodła, a zapasowa zadziałała: nie ukrywamy tego przed użytkownikiem
      return failures.length > 0 ? { ...result, note: `Ocena przez ${a.name === 'browser' ? 'przeglądarkę' : a.name} (zapasowa) — wcześniejsza metoda nie zadziałała: ${failures.join('; ')}` } : result;
    } catch (e) {
      if (e instanceof AssessError && (e.code === 'no-speech' || e.code === 'mic-denied' || e.code === 'no-mic')) throw e;
      causes.push(e instanceof AssessError ? e.code : 'network');
      failures.push(`${a.name}: ${e instanceof AssessError ? `${e.code} (${e.message})` : e instanceof Error ? e.message : String(e)}`);
    }
  }
  const err = new AssessError('unavailable', failures.join('; ') || 'brak dostępnej metody oceny');
  err.causes = causes;
  throw err;
}
